<template>
	<div class="search-tool-popup">
		<ScrollFade>
			<template #scrollContent>
				<div class="search-tool-popup__content_container">
					<div class="search-tool-popup__subheader">
						{{ translate("ui.search_tool_info") }}
					</div>
					<FilterDropdown
						class="search-tool-popup__filter-dropdown"
						:label="filterType"
						:items="[
							FilterItems.ALL,
							FilterItems.BUILDINGS,
							FilterItems.TERRAIN,
							FilterItems.OTHER,
						]"
						@labelSelected="
							(label: FilterItems) => {
								filterType = label;
							}
						"
					/>
					<div class="search-tool-popup__content">
						<TileUpgradeBox
							v-for="(entity, index) in entities"
							:key="index"
							@click="handleClick(entity as TileEntity)"
							:heading="entity.name"
							:type="TileUpgradeBoxTypes.GRID"
							:skin="getBoxTypeSkin(entity.frameName)"
							:iconName="getTileIconName(entity.frameName)"
							:entity="entity as TileEntity"
							:context="UIContexts.SEARCH"
						/>
					</div>
				</div>
			</template>
		</ScrollFade>
	</div>
</template>

<script lang="ts" setup>
import { SAFE_COORDINATE_OUTSIDE_MAP } from "@/config/constants";
import {
	doubledFrameNames,
	doubledFrameNamesGroups,
	tilerepoBigFishingBoatsFrameNames,
	tilerepoBlastFurnacesFrameNames,
	tilerepoCastleRuinsFrameNames,
	tilerepoCobbledRoadsFrameNames,
	tilerepoCoralReefsFrameNames,
	tilerepoDirtRoadsFrameNames,
	tilerepoEventBuildingsFrameNames,
	tilerepoFishingBoatsFrameNames,
	tilerepoFishTrapsFrameNames,
	tilerepoGravelRoadsFrameNames,
	tilerepoLightHousesFrameNames,
	tilerepoLookoutTowersFrameNames,
	tilerepoMediumFishFrameNames,
	tilerepoPearlHuntersFrameNames,
	tilerepoPremiumBuildings,
	tileRepoScoutTowerFrameNames,
	tilerepoShipwrecksFrameNames,
	type FrameName,
} from "@/game/entities/config";
import {
	getAllBuildingsForSearchTool,
	isBuilding,
} from "@/game/entities/config/buildings";
import { getAllRoadsForSearchTool, isRoad } from "@/game/entities/config/roads";
import { getAllSalvageablesForSearchTool } from "@/game/entities/config/salvageables";
import {
	getAllTerrainsForSearchTool,
	isTerrain,
} from "@/game/entities/config/terrain";
import type TileEntity from "@/game/entities/TileEntity";
import useEmitter, { EmitterEvents, TileLabelAction } from "@/hooks/useEmitter";
import useTileRepository from "@/hooks/useTileRepository";
import useRuntimeStore from "@/store/useRuntimeStore";
import { TileLabelSkin } from "@/types/tile";
import {
	TileUpgradeBoxSkinTypes,
	TileUpgradeBoxTypes,
	UIContexts,
} from "@/types/ui";
import { FilterItems } from "@/types/upgrades";
import { getTileIconName } from "@/utils/tileIconName";
import { sortFrameNameByTranslation, translate } from "@/utils/translate";
import { markRaw, onMounted, ref, watch } from "vue";
import FilterDropdown from "../FilterDropdown.vue";
import ScrollFade from "../ScrollFade.vue";
import TileUpgradeBox from "../TileUpgradeBox.vue";

const emit = defineEmits(["closePopup"]);

const entities = ref<Array<TileEntity>>([]);
const tilerepo = markRaw(useTileRepository());
const emitter = markRaw(useEmitter());
const filterType = ref<FilterItems>(FilterItems.ALL);
const buildings = ref<Array<TileEntity>>([]);
const salvageables = ref<Array<TileEntity>>([]);
const terrains = ref<Array<TileEntity>>([]);

const close = (): void => {
	emit("closePopup");
};

const getSearchToolFrameNames = (): void => {
	const buildingsFrameNames = getAllBuildingsForSearchTool();
	const salvageablesFrameNames = getAllSalvageablesForSearchTool();
	const terrainsFrameNames = getAllTerrainsForSearchTool();
	const roadsFrameNames = getAllRoadsForSearchTool();

	// order all alphabetically by framename translation
	buildingsFrameNames.sort(sortFrameNameByTranslation);
	salvageablesFrameNames.sort(sortFrameNameByTranslation);
	terrainsFrameNames.sort(sortFrameNameByTranslation);

	buildings.value = Array.from(
		buildingsFrameNames
			.map((frameName: FrameName): TileEntity | null =>
				tilerepo.createEntityByFrameName(frameName),
			)
			.values(),
	)
		.filter(
			(entity: TileEntity | null): entity is TileEntity =>
				entity !== null &&
				tilerepo.getTileCount2(new Set([entity.frameName]), 1) > 0,
		)
		.filter((entity: TileEntity) => {
			const framesToFilter = [
				tilerepoPearlHuntersFrameNames,
				tilerepoLightHousesFrameNames,
				tileRepoScoutTowerFrameNames,
				tilerepoBlastFurnacesFrameNames,
				tilerepoLookoutTowersFrameNames,
			];
			return framesToFilter.every(
				frameNames =>
					!frameNames.has(entity.frameName) ||
					Array.from(frameNames)[0] === entity.frameName,
			)
				? entity
				: null;
		});
	const roads = Array.from(
		roadsFrameNames
			.map((frameName: FrameName): TileEntity | null =>
				tilerepo.createEntityByFrameName(frameName),
			)
			.values(),
	).filter(
		(entity: TileEntity | null): entity is TileEntity => entity !== null,
	);
	buildings.value.push(...roads);

	salvageables.value = Array.from(
		salvageablesFrameNames
			.map((frameName: FrameName): TileEntity | null =>
				tilerepo.createEntityByFrameName(frameName),
			)
			.values(),
	)
		.filter(
			(entity: TileEntity | null): entity is TileEntity =>
				entity !== null,
		)
		.filter((entity: TileEntity) => {
			const framesToFilter = [
				tilerepoShipwrecksFrameNames,
				tilerepoCastleRuinsFrameNames,
			];

			return framesToFilter.every(
				frameNames =>
					!frameNames.has(entity.frameName) ||
					Array.from(frameNames)[0] === entity.frameName,
			)
				? entity
				: null;
		});
	terrains.value = Array.from(
		terrainsFrameNames
			.map((frameName: FrameName): TileEntity | null =>
				tilerepo.createEntityByFrameName(frameName),
			)
			.values(),
	)
		.filter(
			(entity: TileEntity | null): entity is TileEntity =>
				entity !== null,
		)
		.filter((entity: TileEntity) => {
			const framesToFilter = [
				tilerepoFishingBoatsFrameNames,
				tilerepoBigFishingBoatsFrameNames,
				tilerepoFishTrapsFrameNames,
				tilerepoCoralReefsFrameNames,
				tilerepoMediumFishFrameNames,
			];

			return framesToFilter.every(
				frameNames =>
					!frameNames.has(entity.frameName) ||
					Array.from(frameNames)[0] === entity.frameName,
			)
				? entity
				: null;
		});
};

onMounted((): void => {
	// create entities from framenames
	getSearchToolFrameNames();

	entities.value = getFilteredEntities();
});

const getFilteredEntities = (): Array<TileEntity> => {
	switch (filterType.value) {
		case FilterItems.BUILDINGS:
			return buildings.value as Array<TileEntity>;
		case FilterItems.OTHER:
			return salvageables.value as Array<TileEntity>;
		case FilterItems.TERRAIN:
			return terrains.value as Array<TileEntity>;
		case FilterItems.ALL:
		default:
			return [
				...buildings.value,
				...terrains.value,
				...salvageables.value,
			] as Array<TileEntity>;
	}
};

const handleClick = (entity: TileEntity): void => {
	emitter.emit(EmitterEvents.PhaserUpdateTileLabel, {
		coordinates: {
			x: SAFE_COORDINATE_OUTSIDE_MAP,
			y: SAFE_COORDINATE_OUTSIDE_MAP,
		},
		skin: TileLabelSkin.SEARCH_TOOL,
		action: TileLabelAction.DELETE_ALL,
	});
	let searchToolFrameNames = [entity.frameName];
	if (entity.frameName && doubledFrameNames.has(entity.frameName)) {
		// find group that contain entity.frameName
		doubledFrameNamesGroups.forEach(group => {
			if (group.has(entity.frameName)) {
				searchToolFrameNames = Array.from(group);
			}
		});
	}
	// if entity is road, also show the other road type
	if (isRoad(entity.frameName)) {
		if (tilerepoDirtRoadsFrameNames.has(entity.frameName)) {
			searchToolFrameNames = Array.from(tilerepoDirtRoadsFrameNames);
		} else if (tilerepoGravelRoadsFrameNames.has(entity.frameName)) {
			searchToolFrameNames = Array.from(tilerepoGravelRoadsFrameNames);
		} else if (tilerepoCobbledRoadsFrameNames.has(entity.frameName)) {
			searchToolFrameNames = Array.from(tilerepoCobbledRoadsFrameNames);
		}
	}
	useRuntimeStore().setSearchToolActiveFrameName(searchToolFrameNames);
	searchToolFrameNames.forEach((frameName: FrameName): void => {
		tilerepo.showAllEntitiesSearchTileLabels(frameName);
	});
	close();
};

const getBoxTypeSkin = (frameName: FrameName): TileUpgradeBoxSkinTypes => {
	if (tilerepoEventBuildingsFrameNames.has(frameName))
		return TileUpgradeBoxSkinTypes.BUILDING_EVENT;
	if (isTerrain(frameName)) return TileUpgradeBoxSkinTypes.TERRAIN;
	if (isBuilding(frameName))
		return tilerepoPremiumBuildings.has(frameName)
			? TileUpgradeBoxSkinTypes.BUILDING_PREMIUM
			: TileUpgradeBoxSkinTypes.BUILDING;
	return TileUpgradeBoxSkinTypes.DEFAULT;
};

watch(filterType, (): void => {
	entities.value = getFilteredEntities();
});
</script>

<style lang="scss" scoped>
.search-tool-popup {
	height: 100%;

	&__content_container {
		padding-bottom: 1rem;
	}

	&__subheader {
		color: rgb(242, 220, 181);
		font-size: 0.8125rem;
	}

	&__filter-dropdown {
		display: flex;
		justify-content: center;
		margin: 0.375rem 0 0.5rem;
		:deep(.filter-button) {
			width: 8rem;
		}
	}

	&__content {
		display: grid;
		grid-template-columns: repeat(4, minmax(0, 1fr));
		gap: 0.5rem;
		padding: 0 0.625rem 0 0.5rem;
		width: 100%;
		:deep(.upgrade-box--grid) {
			flex: 1 0 100%; /* Adjusted to fit within grid */
			width: auto;
			.upgrade-box__content-wrapper {
				.content-container {
					.upgrade-box__heading {
						margin-top: 0;
					}
				}
			}
		}
	}

	:deep(.body-content) {
		padding-top: 2.5rem;
	}
}
</style>
